import {
    Alert,
    ColorIndicator,
    ConfirmModal,
    DataTable,
    Icon,
    IconButton,
    Loading,
    PageTitle,
    StyleFunction,
    Surface,
    useAuth,
    useModal,
    usePagination,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQCmpEventsQuery,
    useCmpEventDeleteMutation,
    useCmpEventsQuery,
} from "graphql-sdk";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback } from "react";
import { RootStackScreenProps } from "../../navigation";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";
import { formatDateTime } from "lib";
import { useHandleMutationError } from "../../hooks";

type ScreenProps = RootStackScreenProps<"CMP_EVENTS">;

export function CmpEventListScreen({ navigation: { navigate } }: ScreenProps) {
    const auth = useAuth();
    auth.enforce(["merchant.cmp"]);

    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);
    const theme = useTheme();
    const [t] = useTranslation();
    const { render } = useModal();

    const [cmpEventDelete] = useCmpEventDeleteMutation();
    const { handleMutationError } = useHandleMutationError();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
        initialSortDirection: "ASC",
    });

    const { data, loading, error, refetch } = useCmpEventsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
        },
        fetchPolicy: "no-cache",
    });

    const handleClick = useCallback(
        (externalEventId: string) => {
            navigate("CMP_EVENT", {
                id: externalEventId,
            });
        },
        [navigate]
    );

    const handleDelete = useCallback(
        async (productId: string) => {
            if (
                !(await render(onClose => (
                    <ConfirmModal
                        headerText={t(
                            "backoffice.cmp_event.delete",
                            "Delete CMP event?"
                        )}
                        bodyText={t(
                            "backoffice.cmp_event.delete_explain",
                            "If you choose to delete this CMP event, the event and its products will be deleted. The event and products will be removed from any layouts using the event or products.\n\nAre you sure, you want to delete the event?"
                        )}
                        onClose={onClose}
                    />
                )))
            ) {
                return;
            }

            await handleMutationError(
                async () =>
                    await cmpEventDelete({
                        variables: {
                            id: productId,
                        },
                    }),
                t("backoffice.cmp_event.deleted", "CMP event deleted"),
                async () => {
                    await refetch();
                }
            );
        },
        [handleMutationError, cmpEventDelete, refetch, render, t]
    );

    const renderCmpEventRow = useCallback(
        (item: GQCmpEventsQuery["cmpEvents"]["data"][0], rowIndex) => (
            <DataTable.Row
                style={rowIndex % 2 ? theme.styles.oddRow : undefined}
                onPress={() => handleClick(item.externalId)}
                key={`cmpEventId_${item.id}`}
            >
                <DataTable.Cell style={styles.iconColumn}>
                    <ColorIndicator
                        status={item.active ? "success" : "warning"}
                    />
                </DataTable.Cell>
                <DataTable.Cell>{item.name}</DataTable.Cell>
                <DataTable.Cell>{item.externalId}</DataTable.Cell>
                <DataTable.Cell style={styles.productCountColumn} numeric>
                    {item.products.length}
                </DataTable.Cell>
                <DataTable.Cell>
                    {item.from
                        ? formatDateTime(item.from, locale as AvailableLocale)
                        : null}
                </DataTable.Cell>
                <DataTable.Cell>
                    {item.to
                        ? formatDateTime(item.to, locale as AvailableLocale)
                        : null}
                </DataTable.Cell>
                <DataTable.Cell style={styles.sequenceColumn} numeric>
                    {item.sequence}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.createdAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.updatedAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.iconColumn}>
                    <IconButton
                        color={theme.colors.secondary}
                        name="delete"
                        onPress={() => handleDelete(item.id)}
                        testID={"delete:" + item.name}
                    />
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            handleClick,
            handleDelete,
            locale,
            styles.iconColumn,
            styles.productCountColumn,
            styles.sequenceColumn,
            theme.colors.secondary,
            theme.styles.oddRow,
        ]
    );

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error?.message,
                    }
                )}
            </Alert>
        );
    }

    return (
        <AdminContainer>
            <PageTitle>
                {t("backoffice.cmp_event.cmp_events", "CMP events")}
            </PageTitle>
            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "active" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("active")}
                            style={styles.iconColumn}
                        >
                            {t("backoffice.common.active", "Active")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "name" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("name")}
                        >
                            {t("backoffice.cmp_event.name", "Name")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "externalId"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("externalId")}
                        >
                            {t("backoffice.common.external_id", "External ID")}
                        </DataTable.Title>
                        <DataTable.Title
                            style={styles.productCountColumn}
                            numeric
                        >
                            {t(
                                "backoffice.cmp_event.product_count",
                                "No. products"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "from" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("from")}
                        >
                            {t("backoffice.cmp_event.from", "From")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "to" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("to")}
                        >
                            {t("backoffice.cmp_event.to", "To")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.sequenceColumn} numeric>
                            {t("backoffice.common.sequence", "Sequence")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "createdAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("createdAt")}
                        >
                            {t("common.created_at", "Created at")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "updatedAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("updatedAt")}
                        >
                            {t("common.updated_at", "Updated at")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.iconColumn} numeric>
                            <Icon name="sort" color={theme.colors.black} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? (
                        <Loading />
                    ) : data.cmpEvents.data.length === 0 ? (
                        <DataTable.Row style={theme.styles.oddRow}>
                            <DataTable.Cell>
                                {t(
                                    "backoffice.cmp_event.no_data",
                                    "No events found."
                                )}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ) : (
                        data?.cmpEvents.data.map(renderCmpEventRow)
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.cmpEvents.pagination.pages}
                        itemCount={data?.cmpEvents.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = () => ({
    iconColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 60 },
    productCountColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 150 },
    sequenceColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 180 },
});
